/**
 * BBR-Tabellbanken – sökbar/filtrerbar tabellvy för Boverkets byggregler.
 * Data är strukturerad (siffror + krav), källhänvisning per rad, länk till Boverket.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ExternalLink, ShieldCheck, ChevronRight, Zap, Flame, Accessibility, Droplets, Volume2, Ruler } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_META = {
  energi: { label: "Energi", icon: Zap, color: "text-amber-700 bg-amber-50 border-amber-200" },
  brand: { label: "Brand", icon: Flame, color: "text-red-700 bg-red-50 border-red-200" },
  tillganglighet: { label: "Tillgänglighet", icon: Accessibility, color: "text-purple-700 bg-purple-50 border-purple-200" },
  hygien: { label: "Hygien & inomhusmiljö", icon: Droplets, color: "text-cyan-700 bg-cyan-50 border-cyan-200" },
  buller: { label: "Buller", icon: Volume2, color: "text-blue-700 bg-blue-50 border-blue-200" },
  matt: { label: "Mått & utformning", icon: Ruler, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

const APPLIES_OPTIONS = [
  { value: "", label: "Alla byggnadstyper" },
  { value: "villa", label: "Villa" },
  { value: "attefall", label: "Attefall" },
  { value: "flerbostad", label: "Flerbostadshus" },
  { value: "verksamhet", label: "Verksamhet" },
];

export default function BbrTables() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [appliesTo, setAppliesTo] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.append("category", category);
    if (appliesTo) params.append("applies_to", appliesTo);
    if (query.trim()) params.append("q", query.trim());
    api
      .get(`/bbr/requirements?${params}`)
      .then((r) => mounted && setItems(r.data.items || []))
      .catch(() => mounted && toast.error("Kunde inte hämta BBR-krav"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [category, appliesTo, query]);

  const groups = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      g[it.category] = g[it.category] || [];
      g[it.category].push(it);
    });
    return g;
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <Seo
        title="BBR-Tabellbanken – snabb översikt av byggregler"
        description="Sökbara tabeller med Boverkets byggregler (BBR): U-värden, brandavstånd, dörrbredder, ljudkrav och mycket mer. Källhänvisning till primärkällan."
        path="/bbr-krav"
        keywords="BBR tabell, byggregler krav, u-värde, brandkrav, tillgänglighet, ljudkrav"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-700 mb-2">
          Regelbanken · BBR-krav
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
          BBR-Tabellbanken
        </h1>
        <p className="mt-4 text-base sm:text-lg text-zinc-600 max-w-3xl leading-relaxed">
          Sökbara tabeller med de mest använda kraven ur Boverkets byggregler. Filtrera på byggnadstyp eller kategori. Varje rad länkar till Boverkets primärkälla.
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1">
          <ShieldCheck className="w-3.5 h-3.5" />
          Källhänvisning per rad
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              data-testid="bbr-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sök i BBR-tabeller (t.ex. u-värde, brand, dörrbredd)"
              className="w-full pl-10 pr-4 py-2.5 border border-zinc-300 rounded-sm text-sm bg-white focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none"
            />
          </div>
          <select
            data-testid="bbr-applies-to"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value)}
            className="border border-zinc-300 rounded-sm px-3 py-2.5 text-sm bg-white outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
          >
            {APPLIES_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Kategori-chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategory("")}
            data-testid="bbr-cat-all"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm border transition ${
              category === "" ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-blue-400"
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Alla kategorier
          </button>
          {Object.entries(CATEGORY_META).map(([slug, meta]) => {
            const Icon = meta.icon;
            const active = category === slug;
            return (
              <button
                key={slug}
                onClick={() => setCategory(slug)}
                data-testid={`bbr-cat-${slug}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm border transition ${
                  active ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-blue-400"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="text-center py-16 text-zinc-500">Laddar BBR-krav…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Inga krav matchar filtret.
        </div>
      )}

      {/* Grupperade tabeller */}
      {!loading && Object.entries(groups).map(([cat, rows]) => {
        const meta = CATEGORY_META[cat] || { label: cat, icon: Ruler, color: "" };
        const Icon = meta.icon;
        return (
          <section key={cat} className="mb-8" data-testid={`bbr-group-${cat}`}>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-sm font-medium mb-3 ${meta.color}`}>
              <Icon className="w-4 h-4" />
              {meta.label} · {rows.length} krav
            </div>
            <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-zinc-600">
                      <th className="px-4 py-2.5 font-semibold">Krav</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Värde</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Enhet</th>
                      <th className="px-4 py-2.5 font-semibold">Gäller</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">BBR-avsnitt</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Källa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-zinc-50" data-testid={`bbr-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-zinc-900">{r.title}</div>
                          {r.condition && <div className="mt-0.5 text-xs text-zinc-500">{r.condition}</div>}
                        </td>
                        <td className="px-4 py-3 font-semibold text-blue-700 tabular-nums whitespace-nowrap">
                          {r.value}
                        </td>
                        <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{r.unit}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {r.applies_to.map((a) => (
                              <span
                                key={a}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 text-[10px] uppercase tracking-wider font-semibold"
                              >
                                {a}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">{r.section}</td>
                        <td className="px-4 py-3 text-right">
                          <a
                            href={r.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs"
                            data-testid={`bbr-src-${r.id}`}
                          >
                            Boverket <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );
      })}

      {/* Disclaimer */}
      <div className="mt-10 bg-zinc-50 border border-zinc-200 rounded-sm p-5 text-sm text-zinc-700 leading-relaxed">
        <strong className="text-zinc-900">Notering:</strong> Tabellerna är en förenkling av gällande BBR-krav och är avsedda för snabb översikt. Verifiera alltid mot Boverkets officiella regeltext (länkarna i tabellen) och beakta kommunala tolkningar innan du fattar beslut.
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <a href="https://www.boverket.se/sv/PBL-kunskapsbanken/regler-om-byggande/boverkets-byggregler---bbr/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
            Boverket – BBR i sin helhet <ExternalLink className="w-3 h-3" />
          </a>
          <Link to="/lag/pbl" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
            Läs hela PBL (live) <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
